import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Gift, Loader2, ArrowRight, Heart } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const INCLUDED = [
  'Sessione maternity al tramonto o in studio',
  'Sessione newborn nei primi 5-12 giorni',
  '40+ foto editate in alta risoluzione',
  'Galleria online privata per tutta la famiglia',
];

export default function GiftVoucher() {
  const [searchParams] = useSearchParams();
  const [config, setConfig] = useState(null);
  const [amountType, setAmountType] = useState('package');
  const [customAmount, setCustomAmount] = useState('300');
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ buyer_name: '', buyer_email: '', recipient_name: '', recipient_email: '', message: '', honeypot: '' });

  useEffect(() => {
    api.get('/vouchers/config').then((r) => setConfig(r.data)).catch(() => {});
    if (searchParams.get('cancelled')) toast.info('Pagamento annullato. Puoi riprovare quando vuoi.');
  }, [searchParams]);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const amount = amountType === 'package' ? config?.package_amount : Number(customAmount);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.buyer_name || !form.buyer_email || !form.recipient_name) {
      toast.error('Compila nome, email e destinatario');
      return;
    }
    if (amountType === 'custom' && (isNaN(amount) || amount < config.min_amount || amount > config.max_amount)) {
      toast.error(`Importo tra €${config.min_amount} e €${config.max_amount}`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post('/vouchers/checkout', { ...form, recipient_email: form.recipient_email || null, amount_type: amountType, amount: amountType === 'custom' ? amount : null });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore durante la creazione del pagamento');
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-32 pb-24 min-h-screen bg-[#f6f1ec] text-[#2b2521]" data-testid="gift-voucher-page">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="lg:col-span-5">
          <span className="inline-flex items-center gap-2 text-sm tracking-[0.2em] uppercase text-[#8a6f5f]"><Gift size={14} /> Buono Regalo</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05]">Regala un ricordo<br />che resta.</h1>
          <p className="mt-6 text-base md:text-lg text-[#5b4f48] max-w-md">
            Il regalo perfetto per una futura mamma: il pacchetto <strong className="text-[#2b2521]">Maternity + Newborn</strong>, l'attesa e i primi giorni di vita raccontati con delicatezza. Perfetto da parte di nonni, amici e colleghi.
          </p>
          <ul className="mt-8 space-y-3">
            {INCLUDED.map((item) => (
              <li key={item} className="flex items-start gap-3 text-sm text-[#5b4f48]"><Heart size={16} className="mt-0.5 text-[#c9a27a] shrink-0" />{item}</li>
            ))}
          </ul>
          <p className="mt-8 text-sm text-[#8a6f5f]">
            Il buono arriva subito via email (a te e, se vuoi, direttamente alla destinataria), vale 12 mesi ed è utilizzabile per qualsiasi servizio.
          </p>
        </motion.div>

        <motion.form onSubmit={submit} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }} className="lg:col-span-7 bg-white p-8 lg:p-12 shadow-sm" data-testid="gift-voucher-form">
          <h2 className="font-heading text-2xl mb-6">Scegli l'importo</h2>
          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            <button type="button" onClick={() => setAmountType('package')} className={`text-left p-5 border transition-colors ${amountType === 'package' ? 'border-[#2b2521] bg-[#f6f1ec]' : 'border-[#e5ddd5] hover:border-[#8a6f5f]'}`} data-testid="voucher-amount-package">
              <p className="text-xs uppercase tracking-widest text-[#8a6f5f]">Consigliato</p>
              <p className="font-heading text-xl mt-1">Pacchetto Maternity + Newborn</p>
              <p className="text-2xl font-bold mt-2">€ {config?.package_amount ?? 650}</p>
            </button>
            <button type="button" onClick={() => setAmountType('custom')} className={`text-left p-5 border transition-colors ${amountType === 'custom' ? 'border-[#2b2521] bg-[#f6f1ec]' : 'border-[#e5ddd5] hover:border-[#8a6f5f]'}`} data-testid="voucher-amount-custom">
              <p className="text-xs uppercase tracking-widest text-[#8a6f5f]">Importo libero</p>
              <p className="font-heading text-xl mt-1">Contributo al servizio</p>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-2xl font-bold">€</span>
                <Input type="number" min={config?.min_amount ?? 100} max={config?.max_amount ?? 2000} step="10" value={customAmount} onChange={(e) => setCustomAmount(e.target.value)} onFocus={() => setAmountType('custom')} className="w-28" data-testid="voucher-custom-amount-input" />
              </div>
            </button>
          </div>

          <h2 className="font-heading text-2xl mb-6">I tuoi dati</h2>
          <div className="grid sm:grid-cols-2 gap-5 mb-6">
            <div><Label className="mb-2 block">Il tuo nome *</Label><Input name="buyer_name" value={form.buyer_name} onChange={onChange} required data-testid="voucher-buyer-name" /></div>
            <div><Label className="mb-2 block">La tua email *</Label><Input type="email" name="buyer_email" value={form.buyer_email} onChange={onChange} required data-testid="voucher-buyer-email" /></div>
          </div>
          <h2 className="font-heading text-2xl mb-6">Per chi è il regalo</h2>
          <div className="grid sm:grid-cols-2 gap-5 mb-6">
            <div><Label className="mb-2 block">Nome della destinataria *</Label><Input name="recipient_name" value={form.recipient_name} onChange={onChange} required data-testid="voucher-recipient-name" /></div>
            <div><Label className="mb-2 block">Email destinataria (opzionale)</Label><Input type="email" name="recipient_email" value={form.recipient_email} onChange={onChange} placeholder="Se vuoi che riceva subito il buono" data-testid="voucher-recipient-email" /></div>
          </div>
          <div className="mb-8">
            <Label className="mb-2 block">Messaggio di auguri (opzionale)</Label>
            <Textarea name="message" value={form.message} onChange={onChange} rows={3} maxLength={500} placeholder="Un pensiero che comparirà sul buono..." className="resize-none" data-testid="voucher-message" />
          </div>
          <input type="text" name="honeypot" value={form.honeypot} onChange={onChange} className="hidden" tabIndex={-1} autoComplete="off" />

          <Button type="submit" size="lg" disabled={submitting || !config} className="w-full bg-[#2b2521] text-[#f6f1ec] hover:bg-[#5b4f48]" data-testid="voucher-pay-btn">
            {submitting ? <Loader2 className="mr-2 animate-spin" size={18} /> : <Gift className="mr-2" size={18} />}
            {submitting ? 'Reindirizzamento al pagamento...' : `Paga € ${isNaN(amount) ? '—' : Number(amount).toFixed(0)} e ricevi il buono`}
            {!submitting && <ArrowRight className="ml-2" size={18} />}
          </Button>
          <p className="text-xs text-center text-[#8a6f5f] mt-4">Pagamento sicuro con carta tramite Stripe. Riceverai il buono via email entro pochi secondi.</p>
        </motion.form>
      </div>
    </div>
  );
}
